// Redesigned display boards (opt-in per brand via REACT_APP_BRAND_DASHBOARD=modern).
// Covers every board layout and follows the Theme setting (light / dark / dark_blue).
// Presentation only: DisplayBoardPage owns polling, audio and data, and passes
// the same lists it gives the classic layouts.
import React, { createContext, useContext, useEffect, useState } from "react";
import BRAND from "../../brand";

const P = BRAND.colorPrimary;
const A = BRAND.colorAccent;

const LIGHT = {
  dark: false,
  bg: "#F1F6FB", card: "#FFFFFF", line: "#E3EAF2", ink: "#0F2A47", muted: "#5B6B80", faint: "#A9B6C6",
  shadow: "0 6px 22px rgba(15,42,71,0.07)", chipShadow: "0 2px 8px rgba(15,42,71,0.06)",
  pill: "#F1F5F9", iconBg: "#EAF1F8",
  waitBg: "#E6F1FA", waitFg: P, servBg: "#E3F5F3", servFg: "#0E8F86", doneBg: "#E8F5E4", doneFg: "#2F8A3D",
  nextBg: "#ECE9FA", nextFg: "#4C3FA0", recBg: "#E4F4E8", recFg: "#1F7A4D", bellBg: "#FFF3DC", bellFg: "#D9930D",
  availBg: "#E6F5E8", availFg: "#1F7A3A", availDot: "#16A34A", servingBg: "#E3EFFA", servingFg: "#1D5E96",
  breakBg: "#FFF4DC", breakFg: "#9A6A00", footer: "#0B2A4A", overlay: "rgba(11,42,74,0.78)",
  emergencyBg: "#FDECEC", priorityBg: "#F2F8E0", apptBg: "#F0EBFB", apptFg: "#6D4FC2", stripe: "rgba(255,255,255,0.9)",
};
const DARK = {
  dark: true,
  bg: "#0A1424", card: "#0F1D35", line: "#1B2D4A", ink: "#EAF1FA", muted: "#8FA3BD", faint: "#5C7192",
  shadow: "0 6px 22px rgba(0,0,0,0.35)", chipShadow: "0 2px 8px rgba(0,0,0,0.3)",
  pill: "#16253F", iconBg: "#16294A",
  waitBg: "#12304F", waitFg: "#7FB6E3", servBg: "#0F3A3A", servFg: "#2DD4BF", doneBg: "#12341F", doneFg: "#4ADE80",
  nextBg: "#241F4F", nextFg: "#B7ADFF", recBg: "#12372A", recFg: "#6EE7A8", bellBg: "#3A2E10", bellFg: "#F5B942",
  availBg: "#10321F", availFg: "#4ADE80", availDot: "#22C55E", servingBg: "#12304F", servingFg: "#8CBCE0",
  breakBg: "#3A2E10", breakFg: "#F5B942", footer: "#061225", overlay: "rgba(3,8,20,0.85)",
  emergencyBg: "#3A1818", priorityBg: "#2A3410", apptBg: "#2B2150", apptFg: "#C4B5FD", stripe: "rgba(15,29,53,0.9)",
};
const DARK_BLUE = { ...DARK, bg: "#071433", card: "#0B1C40", line: "#173063", pill: "#12285A", iconBg: "#142C5E" };

const TC = createContext(LIGHT);
const useT = () => useContext(TC);
const themeFor = (name) => (name === "light" ? LIGHT : name === "dark_blue" ? DARK_BLUE : DARK);

const Svg = ({ children, size = 24, sw = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconPeople = (p) => <Svg {...p}><circle cx="9" cy="8" r="3.2" /><path d="M2.8 20c.4-3.3 3-5.2 6.2-5.2s5.8 1.9 6.2 5.2" /><circle cx="17" cy="9" r="2.4" /><path d="M17 14.2c2.4 0 4.1 1.5 4.4 3.8" /></Svg>;
const IconDesk = (p) => <Svg {...p}><rect x="6" y="3" width="12" height="9" rx="1.5" /><path d="M3 21v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5" /><path d="M10 7h4" /></Svg>;
const IconCheck = (p) => <Svg {...p}><circle cx="12" cy="12" r="9.5" /><path d="m7.8 12.4 3 3 5.6-6" /></Svg>;
const IconBroadcast = (p) => <Svg {...p}><circle cx="12" cy="12" r="1.8" /><path d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 8.2a5.4 5.4 0 0 1 0 7.6M5.4 5.4a9.4 9.4 0 0 0 0 13.2M18.6 5.4a9.4 9.4 0 0 1 0 13.2" /></Svg>;
const IconClock = (p) => <Svg {...p}><circle cx="12" cy="12" r="9.5" /><path d="M12 7v5.2l3.4 2" /></Svg>;
const IconBell = (p) => <Svg {...p}><path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.6 2H4.4z" /><path d="M10 21a2.2 2.2 0 0 0 4 0" /></Svg>;
const IconBuilding = (p) => <Svg {...p}><path d="M4 21V9l8-5 8 5v12" /><path d="M9 21v-6h6v6M8 11h.01M12 11h.01M16 11h.01" /></Svg>;
const IconInfo = (p) => <Svg {...p}><circle cx="12" cy="12" r="9.5" /><path d="M12 11v5.5M12 7.6h.01" /></Svg>;
const IconPulse = (p) => <Svg {...p}><path d="M2 12h4l2.5-6 4 12 2.5-6H22" /></Svg>;
const IconFilm = (p) => <Svg {...p}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M3 15h18M8 4v16M16 4v16" /></Svg>;

const PRIORITY = { emergency: "#EF4444", priority: A };
const FONT = "'DM Sans','Segoe UI',sans-serif";

function Clock({ size = "2em" }) {
  const T = useT();
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  return (
    <div style={{ textAlign: "right", flexShrink: 0 }}>
      <div style={{ fontSize: size, fontWeight: 800, color: T.ink, letterSpacing: "-0.5px", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
        {t.toLocaleTimeString("en-GH", { hour: "2-digit", minute: "2-digit" }).toUpperCase()}
      </div>
      <div style={{ fontSize: "0.7em", color: T.muted, marginTop: 4 }}>
        {t.toLocaleDateString("en-GH", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
      </div>
    </div>
  );
}

function StatChip({ icon, value, label, bg, fg, small }) {
  const T = useT();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: small ? 8 : 12, background: bg, borderRadius: 16, padding: small ? "8px 14px" : "10px 20px", minWidth: small ? 0 : 150, boxShadow: T.chipShadow }}>
      <span style={{ color: fg, display: "flex" }}>{icon}</span>
      <div>
        <div style={{ fontSize: small ? "1.2em" : "1.5em", fontWeight: 800, color: T.ink, lineHeight: 1 }}>{value ?? 0}</div>
        <div style={{ fontSize: "0.72em", fontWeight: 600, color: fg, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

const Logo = ({ height = 46 }) => {
  const src = BRAND.logoMark ? `${process.env.PUBLIC_URL}/${BRAND.logoMark}` : `${process.env.PUBLIC_URL}/logo192.png`;
  return <img src={src} alt={BRAND.name} style={{ height, width: "auto", objectFit: "contain", flexShrink: 0 }} />;
};

const useCard = () => { const T = useT(); return { background: T.card, border: `1px solid ${T.line}`, borderRadius: 20, boxShadow: T.shadow }; };

function Unlock({ onUnlock }) {
  const T = useT();
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: T.overlay, backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={onUnlock}>
      <div style={{ background: T.card, color: T.ink, borderRadius: 24, padding: "44px 52px", textAlign: "center", boxShadow: "0 30px 80px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "inline-flex", color: P, marginBottom: 14 }}><IconBroadcast size={52} /></div>
        <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Tap to activate display board</div>
        <div style={{ fontSize: 14, color: T.muted, maxWidth: 320, lineHeight: 1.6 }}>Browsers require a user interaction to enable audio.</div>
      </div>
    </div>
  );
}

const ErrorRibbon = ({ error, style }) => error ? (
  <div style={{ background: "#FDECEC", color: "#B42318", fontSize: "0.8em", fontWeight: 600, padding: "6px 20px", textAlign: "center", ...style }}>⚠ {error}</div>
) : null;

function SideCard({ title, icon, bg, fg, children }) {
  const T = useT(); const card = useCard();
  return (
    <div style={{ ...card, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
      <div style={{ background: bg, padding: "14px 20px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <span style={{ color: fg, display: "flex" }}>{icon}</span>
        <span style={{ fontSize: "1.15em", fontWeight: 800, color: T.dark ? fg : T.ink }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

const Empty = ({ icon, bg, fg, text }) => {
  const T = useT();
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 16 }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div style={{ color: T.ink, fontSize: "0.95em", textAlign: "center" }}>{text}</div>
    </div>
  );
};

function Bench() {
  const T = useT();
  return (
    <svg width="340" height="190" viewBox="0 0 340 190" fill="none">
      <path d="M30 150c-24-4-26-48 14-62 8-34 52-52 86-34 22-20 66-14 80 14 40-2 62 30 50 58-4 12-12 22-24 24z" fill={P} opacity={T.dark ? 0.16 : 0.10} />
      <path d="M62 150v-34" stroke="#3D8A55" strokeWidth="3" strokeLinecap="round" />
      <path d="M62 118c-18-4-26-22-18-40 14 2 22 16 18 40z" fill="#4DAA68" />
      <path d="M62 118c14-8 22-24 12-42-12 10-16 26-12 42z" fill="#6DC587" />
      <rect x="50" y="128" width="26" height="24" rx="4" fill={P} opacity="0.85" />
      {[130, 190, 250].map(x => <rect key={x} x={x} y="92" width="52" height="40" rx="12" fill={P} opacity="0.55" />)}
      <rect x="122" y="128" width="188" height="14" rx="7" fill={P} opacity="0.8" />
      <path d="M140 142v28M292 142v28" stroke={P} strokeWidth="6" strokeLinecap="round" opacity="0.8" />
    </svg>
  );
}

const Badges = ({ s, T }) => (
  <>
    {s.priority && s.priority !== "normal" && (
      <span style={{ fontSize: "0.8em", fontWeight: 800, padding: "5px 12px", borderRadius: 8, color: PRIORITY[s.priority], background: s.priority === "emergency" ? T.emergencyBg : T.priorityBg }}>
        {s.priority === "emergency" ? "EMERGENCY" : "PRIORITY"}
      </span>
    )}
    {s.is_appointment && <span style={{ fontSize: "0.8em", fontWeight: 800, padding: "5px 12px", borderRadius: 8, color: T.apptFg, background: T.apptBg }}>APPOINTMENT</span>}
  </>
);

const nowHeader = { background: `linear-gradient(90deg, #0C5A9E, ${P})`, padding: "14px 24px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 };

// ── Header shared by the full-screen layouts ────────────────────────────────
function BoardHeader({ settings, summary, showDone = true }) {
  const T = useT();
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, padding: "12px 28px", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, minWidth: 0 }}>
        <Logo />
        <div style={{ fontSize: "1.05em", fontWeight: 800, lineHeight: 1.25, maxWidth: 230, color: T.ink }}>{BRAND.name}</div>
        <div style={{ width: 1, alignSelf: "stretch", background: T.line, margin: "4px 4px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 46, height: 46, borderRadius: "50%", background: T.iconBg, color: P, display: "flex", alignItems: "center", justifyContent: "center" }}><IconPeople size={24} /></div>
          <div>
            <div style={{ fontSize: "1.1em", fontWeight: 800, color: T.ink }}>Queue Display</div>
            <div style={{ fontSize: "0.75em", color: T.muted }}>{settings.facility_name || "Live Queue Status"}</div>
          </div>
        </div>
      </div>
      {settings.board_show_stats !== false && (
        <div style={{ display: "flex", gap: 14 }}>
          <StatChip icon={<IconPeople size={30} />} value={summary.total_waiting} label="Waiting" bg={T.waitBg} fg={T.waitFg} />
          <StatChip icon={<IconDesk size={30} />} value={summary.total_serving} label="Now Serving" bg={T.servBg} fg={T.servFg} />
          {showDone && <StatChip icon={<IconCheck size={30} />} value={summary.total_completed} label="Served Today" bg={T.doneBg} fg={T.doneFg} />}
        </div>
      )}
      <Clock />
    </div>
  );
}

function Announcement({ settings }) {
  if (!(settings.board_announcement_active && settings.board_announcement)) return null;
  return (
    <div style={{ margin: "0 28px 10px", background: P, color: "#fff", borderRadius: 14, padding: "10px 20px", height: 42, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 12, overflow: "hidden", flexShrink: 0 }}>
      <IconBroadcast size={20} />
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ display: "inline-block", whiteSpace: "nowrap", fontWeight: 600, animation: "eq-marquee 22s linear infinite" }}>{settings.board_announcement}</div>
      </div>
    </div>
  );
}

function BoardFooter({ settings }) {
  const poweredBy = settings.powered_by_text
    ? settings.powered_by_text
    : settings.board_show_powered_by !== false ? `Powered by ${settings.branding_name || BRAND.name}` : "";
  const T = useT();
  return (
    <div style={{ background: T.footer, color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "16px 28px", flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, fontWeight: 600 }}>
        <IconInfo size={22} />
        <span>{settings.footer_message || "Please listen for your number and proceed to the assigned counter. Thank you!"}</span>
      </div>
      {poweredBy && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.85em", fontWeight: 600, opacity: 0.9 }}>
          <IconPulse size={26} />{poweredBy}
        </div>
      )}
    </div>
  );
}

// ── Full screen (and Full screen + video) ───────────────────────────────────
function FullLayout(props) {
  const {
    layout, Video, settings, summary, nowServing, upNext, recentlyCalled, allStations,
    visibleStations, stationPage, setStationPage, totalStationPages,
    allCountersAvailable, upNextScrollRef, unlocked,
  } = props;
  const T = useT(); const card = useCard();
  const withVideo = layout === "full_video";
  const pageBtn = { background: T.pill, border: `1px solid ${T.line}`, borderRadius: 6, width: 26, height: 26, cursor: "pointer", fontSize: 15, color: T.ink, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 };

  return (
    <>
      <BoardHeader settings={settings} summary={summary} />
      <Announcement settings={settings} />

      <div style={{ flex: 1, display: "flex", gap: 20, padding: "6px 28px 14px", minHeight: 0 }}>
        <div style={{ ...card, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
          <div style={nowHeader}>
            <span style={{ color: "#fff", display: "flex" }}><IconBroadcast size={30} /></span>
            <span style={{ fontSize: "1.5em", fontWeight: 800, color: "#fff" }}>Now Serving</span>
          </div>
          {withVideo ? (
            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
              <Video url={settings.board_background_video_url} mode="absolute" soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap", padding: "12px 24px", background: T.stripe, backdropFilter: "blur(6px)", borderBottom: `1px solid ${T.line}` }}>
                {nowServing.length > 0 ? nowServing.slice(0, 3).map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: "1.9em", fontWeight: 900, fontVariantNumeric: "tabular-nums", lineHeight: 1, color: PRIORITY[s.priority] || T.ink }}>{s.ticket_number}</span>
                    <Badges s={s} T={T} />
                    <span style={{ fontSize: "0.9em", fontWeight: 700, color: T.muted }}>{s.stationName} · {s.counterName}</span>
                  </div>
                )) : <span style={{ fontSize: "1em", fontWeight: 600, color: T.muted }}>No patients currently being served</span>}
              </div>
              {!settings.board_background_video_url && (
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: T.muted, zIndex: 1 }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: T.iconBg, color: P, display: "flex", alignItems: "center", justifyContent: "center" }}><IconFilm size={34} /></div>
                  <span style={{ fontSize: "0.9em" }}>No background video configured — set one in Settings → Display Board.</span>
                </div>
              )}
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: "auto", position: "relative", display: "flex", flexDirection: "column" }}>
              {nowServing.length === 0 ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 24 }}>
                  <Bench />
                  <div style={{ fontSize: "1.9em", fontWeight: 800, textAlign: "center", color: T.ink }}>No patients currently being served</div>
                  {allCountersAvailable && <div style={{ fontSize: "1.1em", color: T.muted }}>All counters are currently available</div>}
                </div>
              ) : nowServing.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 22, padding: "18px 28px", borderBottom: `1px solid ${T.line}` }}>
                  <div style={{ fontSize: "3.4em", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1, minWidth: 170, fontVariantNumeric: "tabular-nums", color: PRIORITY[s.priority] || T.ink }}>{s.ticket_number}</div>
                  <div style={{ fontSize: "1.8em", color: T.faint }}>→</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "1.4em", fontWeight: 800, color: T.ink }}>{s.stationName}</div>
                    <div style={{ fontSize: "1em", color: P, fontWeight: 600, marginTop: 2 }}>{s.counterName}</div>
                    {s.staff_name && settings.board_show_staff_names && <div style={{ fontSize: "0.85em", color: T.muted, marginTop: 2 }}>{s.staff_name}</div>}
                  </div>
                  <Badges s={s} T={T} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ width: 350, flexShrink: 0, display: "flex", flexDirection: "column", gap: 20, minHeight: 0 }}>
          <SideCard title="Up Next" icon={<IconPeople size={26} />} bg={T.nextBg} fg={T.nextFg}>
            <div ref={upNextScrollRef} style={{ flex: 1, overflowY: "hidden", display: "flex", flexDirection: "column" }}>
              {settings.board_show_up_next !== false && upNext.length > 0 ? upNext.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", borderBottom: `1px solid ${T.line}` }}>
                  <span style={{ fontSize: "0.8em", color: T.muted, minWidth: 18, fontWeight: 700 }}>{i + 1}</span>
                  <span style={{ fontSize: "1.2em", fontWeight: 800, color: T.nextFg, flex: 1, fontVariantNumeric: "tabular-nums" }}>{t.ticket_number}</span>
                  <span style={{ fontSize: "0.75em", color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 110, textAlign: "right" }}>{t.station_name}</span>
                  {t.priority !== "normal" && <span style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY[t.priority] || A }} />}
                </div>
              )) : <Empty icon={<IconPeople size={30} />} bg={T.nextBg} fg={T.nextFg} text="No patients queued" />}
            </div>
          </SideCard>
          <SideCard title="Recently Called" icon={<IconClock size={26} />} bg={T.recBg} fg={T.recFg}>
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
              {recentlyCalled.length === 0 ? (
                <Empty icon={<IconBell size={30} />} bg={T.bellBg} fg={T.bellFg} text="No calls yet today" />
              ) : recentlyCalled.map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "11px 20px", borderBottom: `1px solid ${T.line}` }}>
                  <div>
                    <div style={{ fontSize: "1.15em", fontWeight: 800, color: T.recFg, fontVariantNumeric: "tabular-nums" }}>{r.ticket_number}</div>
                    <div style={{ fontSize: "0.8em", marginTop: 1, color: T.ink }}>{r.station_name}</div>
                    {r.counter_name && <div style={{ fontSize: "0.72em", color: T.muted }}>{r.counter_name}</div>}
                  </div>
                  <div style={{ fontSize: "0.72em", color: T.muted, flexShrink: 0 }}>{r.called_at}</div>
                </div>
              ))}
            </div>
          </SideCard>
        </div>
      </div>

      <div style={{ ...card, margin: "0 28px 14px", flexShrink: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", borderBottom: `1px solid ${T.line}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: T.servBg, color: T.servFg, display: "flex", alignItems: "center", justifyContent: "center" }}><IconDesk size={22} /></div>
            <span style={{ fontSize: "1.25em", fontWeight: 800, color: T.ink }}>Station Status</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {[["Available", T.availDot], ["Serving", P], ["Paused", "#F5A400"], ["Offline", "#94A3B8"]].map(([l, c]) => (
              <span key={l} style={{ display: "flex", alignItems: "center", gap: 7, background: T.pill, borderRadius: 999, padding: "5px 14px", fontSize: "0.75em", fontWeight: 600, color: T.muted }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />{l}
              </span>
            ))}
            {totalStationPages > 1 && (
              <span style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 6, fontSize: "0.75em", color: T.muted }}>
                {stationPage + 1}/{totalStationPages}
                <button onClick={() => setStationPage(p => Math.max(0, p - 1))} disabled={stationPage === 0} style={pageBtn}>‹</button>
                <button onClick={() => setStationPage(p => Math.min(totalStationPages - 1, p + 1))} disabled={stationPage >= totalStationPages - 1} style={pageBtn}>›</button>
              </span>
            )}
          </div>
        </div>
        <div style={{ padding: "10px 24px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
          {visibleStations.map((st, i) => (
            <div key={st.id} style={{ display: "flex", alignItems: "center", gap: 20, borderLeft: `4px solid ${i % 2 ? A : "#14B8A6"}`, paddingLeft: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 220 }}>
                <div style={{ width: 46, height: 46, borderRadius: "50%", background: T.pill, color: T.ink, display: "flex", alignItems: "center", justifyContent: "center" }}><IconBuilding size={24} /></div>
                <div>
                  <div style={{ fontSize: "1.15em", fontWeight: 800, color: T.ink }}>{st.name}</div>
                  <div style={{ fontSize: "0.8em", color: T.muted }}>{st.counters?.length || 0} Counter{(st.counters?.length || 0) === 1 ? "" : "s"}</div>
                </div>
              </div>
              <div style={{ width: 1, alignSelf: "stretch", background: T.line }} />
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", flex: 1 }}>
                {st.counters?.map(c => <CounterTile key={c.id} c={c} />)}
                {(!st.counters || st.counters.length === 0) && <span style={{ color: T.muted, fontSize: "0.8em" }}>No counters</span>}
              </div>
            </div>
          ))}
          {allStations.length === 0 && <div style={{ color: T.muted, fontSize: "0.85em" }}>No stations configured.</div>}
        </div>
      </div>

      <BoardFooter settings={settings} />
    </>
  );
}

function CounterTile({ c, minWidth = 200 }) {
  const T = useT();
  const s = c.is_away ? { bg: T.breakBg, dot: "#F5A400", fg: T.breakFg, t: "On break" }
    : c.serving ? { bg: T.servingBg, dot: P, fg: T.servingFg, t: `Serving ${c.serving.ticket_number}` }
    : { bg: T.availBg, dot: T.availDot, fg: T.availFg, t: "Available" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, background: s.bg, borderRadius: 14, padding: "10px 22px", minWidth }}>
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: "1em", fontWeight: 800, color: T.ink }}>{c.name}</div>
        <div style={{ fontSize: "0.85em", fontWeight: 600, color: s.fg }}>{s.t}</div>
      </div>
    </div>
  );
}

// ── Video grid: counter tiles + large video + scrolling station ticker ──────
function VideoGridLayout({ Video, settings, summary, allStations, nowServing, upNext, unlocked }) {
  const T = useT(); const card = useCard();
  const counters = [];
  allStations.forEach(st => (st.counters || []).forEach(c => counters.push({ ...c, stationName: st.name })));
  const grid = counters.slice(0, 6);
  const ticker = (nowServing.length > 0 ? nowServing : upNext).map(t => `${t.stationName || t.station_name} · ${t.ticket_number}`);
  return (
    <>
      <BoardHeader settings={settings} summary={summary} showDone={false} />
      <div style={{ flex: 1, display: "flex", gap: 20, padding: "6px 28px 14px", minHeight: 0 }}>
        <div style={{ width: "38%", minWidth: 280, display: "grid", gridTemplateColumns: "1fr 1fr", gridAutoRows: "1fr", gap: 14 }}>
          {grid.length === 0 ? (
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>No counters configured.</div>
          ) : grid.map((c, i) => {
            const col = c.is_away ? "#F5A400" : c.serving ? P : T.availDot;
            return (
              <div key={c.id} style={{ ...card, borderRadius: 18, padding: "14px 18px", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden", borderTop: `4px solid ${col}` }}>
                <div style={{ fontSize: "0.72em", fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.stationName} · {c.name}</div>
                {c.is_away ? <div style={{ fontSize: "1.1em", color: T.breakFg, fontWeight: 800, marginTop: 6 }}>On break</div>
                  : c.serving ? <div style={{ fontSize: "2.2em", fontWeight: 900, color: PRIORITY[c.serving.priority] || T.ink, fontVariantNumeric: "tabular-nums", marginTop: 2, lineHeight: 1.1 }}>{c.serving.ticket_number}</div>
                  : <div style={{ fontSize: "1.1em", fontWeight: 800, color: T.availFg, marginTop: 6 }}>Available</div>}
              </div>
            );
          })}
        </div>
        <div style={{ ...card, flex: 1, position: "relative", overflow: "hidden" }}>
          {settings.board_background_video_url ? (
            <Video url={settings.board_background_video_url} mode="absolute" soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: T.iconBg, color: P, display: "flex", alignItems: "center", justifyContent: "center" }}><IconFilm size={34} /></div>
              <div style={{ color: T.muted }}>No promo video configured</div>
            </div>
          )}
        </div>
      </div>
      <div style={{ background: T.footer, color: "#fff", display: "flex", alignItems: "center", gap: 18, padding: "0 28px", height: 64, flexShrink: 0, overflow: "hidden" }}>
        <IconInfo size={22} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ display: "inline-block", whiteSpace: "nowrap", fontWeight: 700, animation: "eq-marquee 26s linear infinite" }}>
            {ticker.length > 0 ? ticker.join("     •     ") : "No patients currently queued"}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Compact overlay: slim bar at top or bottom (transparent elsewhere) ──────
function TickerLayout({ position, Video, settings, summary, nowServing, upNext, unlocked }) {
  const T = useT();
  const current = nowServing[0];
  return (
    <>
      <Video url={settings.board_background_video_url} soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
      <div style={{ position: "fixed", left: 0, right: 0, [position]: 0, height: 116, padding: 10, boxSizing: "border-box", fontFamily: FONT, zIndex: 5 }}>
        <div style={{ height: "100%", display: "flex", alignItems: "center", gap: 20, padding: "0 22px", background: T.card, border: `1px solid ${T.line}`, borderRadius: 20, boxShadow: "0 10px 30px rgba(0,0,0,0.25)", overflow: "hidden" }}>
          <Logo height={46} />
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: "0.85em", fontWeight: 800, color: T.ink, maxWidth: 200 }}>{settings.facility_name || BRAND.name}</div>
            <div style={{ marginTop: 4 }}><Clock size="1.5em" /></div>
          </div>
          <div style={{ width: 1, height: "60%", background: T.line, flexShrink: 0 }} />
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, background: `linear-gradient(90deg, #0C5A9E, ${P})`, color: "#fff", borderRadius: 12, padding: "8px 14px", fontSize: "0.8em", fontWeight: 800 }}><IconBroadcast size={20} />Now Serving</span>
            {current ? (
              <>
                <span style={{ fontSize: "2.2em", fontWeight: 900, fontVariantNumeric: "tabular-nums", lineHeight: 1, color: PRIORITY[current.priority] || T.ink }}>{current.ticket_number}</span>
                <Badges s={current} T={T} />
                <span style={{ fontSize: "0.9em", color: P, fontWeight: 700 }}>{current.stationName} · {current.counterName}</span>
              </>
            ) : <span style={{ fontSize: "0.9em", color: T.muted }}>No patients currently being served</span>}
          </div>
          <div style={{ width: 1, height: "60%", background: T.line, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, background: T.nextBg, color: T.nextFg, borderRadius: 12, padding: "8px 14px", fontSize: "0.8em", fontWeight: 800, flexShrink: 0 }}><IconPeople size={20} />Up Next</span>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <div style={{ fontSize: "0.95em", fontWeight: 700, color: T.ink, whiteSpace: "nowrap", display: "inline-block", animation: "eq-marquee 22s linear infinite" }}>
                {upNext.length > 0 ? upNext.map(t => t.ticket_number).join("   •   ") : "No patients queued"}
              </div>
            </div>
          </div>
          {settings.board_show_stats !== false && (
            <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
              <StatChip small icon={<IconPeople size={22} />} value={summary.total_waiting} label="Waiting" bg={T.waitBg} fg={T.waitFg} />
              <StatChip small icon={<IconDesk size={22} />} value={summary.total_serving} label="Serving" bg={T.servBg} fg={T.servFg} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Compact overlay: side panel (left or right) ──────────────────────────────
function SidePanelLayout({ position, Video, settings, summary, nowServing, upNext, recentlyCalled, unlocked }) {
  const T = useT();
  const sec = { background: T.card, border: `1px solid ${T.line}`, borderRadius: 18, overflow: "hidden", boxShadow: T.shadow };
  const label = (icon, text, bg, fg) => (
    <div style={{ background: bg, color: fg, padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, fontSize: "0.95em", fontWeight: 800 }}>{icon}{text}</div>
  );
  return (
    <>
      <Video url={settings.board_background_video_url} soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
      <div style={{ position: "fixed", top: 0, bottom: 0, [position]: 0, width: 372, padding: 12, boxSizing: "border-box", fontFamily: FONT, zIndex: 5, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ ...sec, display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", flexShrink: 0 }}>
          <Logo height={40} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "0.9em", fontWeight: 800, color: T.ink, lineHeight: 1.25 }}>{settings.facility_name || BRAND.name}</div>
            <div style={{ fontSize: "0.7em", color: T.muted }}>Queue Display</div>
          </div>
        </div>
        {settings.board_show_stats !== false && (
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            <div style={{ flex: 1 }}><StatChip small icon={<IconPeople size={22} />} value={summary.total_waiting} label="Waiting" bg={T.waitBg} fg={T.waitFg} /></div>
            <div style={{ flex: 1 }}><StatChip small icon={<IconDesk size={22} />} value={summary.total_serving} label="Serving" bg={T.servBg} fg={T.servFg} /></div>
          </div>
        )}
        <div style={{ ...sec, flexShrink: 0 }}>
          <div style={{ ...nowHeader, padding: "10px 16px", gap: 8, fontSize: "0.95em", fontWeight: 800, color: "#fff" }}><IconBroadcast size={20} />Now Serving</div>
          <div style={{ padding: "6px 16px 10px" }}>
            {nowServing.length === 0 ? (
              <div style={{ padding: "10px 0", fontSize: "0.85em", color: T.muted }}>No patients currently being served</div>
            ) : nowServing.slice(0, 3).map((s, i) => (
              <div key={i} style={{ padding: "8px 0", borderBottom: i < Math.min(nowServing.length, 3) - 1 ? `1px solid ${T.line}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: "2em", fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums", color: PRIORITY[s.priority] || T.ink }}>{s.ticket_number}</span>
                  <Badges s={s} T={T} />
                </div>
                <div style={{ fontSize: "0.8em", color: P, fontWeight: 700, marginTop: 3 }}>{s.stationName} · {s.counterName}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...sec, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {label(<IconPeople size={20} />, "Up Next", T.nextBg, T.nextFg)}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {upNext.length === 0 ? <div style={{ padding: "12px 16px", fontSize: "0.85em", color: T.muted }}>No patients queued</div>
              : upNext.slice(0, 6).map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: `1px solid ${T.line}` }}>
                  <span style={{ fontSize: "1.05em", fontWeight: 800, color: T.nextFg, fontVariantNumeric: "tabular-nums" }}>{t.ticket_number}</span>
                  <span style={{ fontSize: "0.75em", color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.station_name}</span>
                </div>
              ))}
            {label(<IconClock size={20} />, "Recently Called", T.recBg, T.recFg)}
            {recentlyCalled.length === 0 ? <div style={{ padding: "12px 16px", fontSize: "0.85em", color: T.muted }}>No calls yet today</div>
              : recentlyCalled.slice(0, 6).map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 16px", borderBottom: `1px solid ${T.line}` }}>
                  <span style={{ fontSize: "1em", fontWeight: 800, color: T.recFg, fontVariantNumeric: "tabular-nums" }}>{r.ticket_number}</span>
                  <span style={{ fontSize: "0.75em", color: T.muted }}>{r.called_at}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default function DisplayBoardModern(props) {
  const { layout, settings, fontScale = 1, unlocked, onUnlock, error } = props;
  const T = themeFor(settings.board_theme);
  const overlay = layout === "ticker_top" || layout === "ticker_bottom" || layout === "side_left" || layout === "side_right";

  let body;
  if (layout === "ticker_top" || layout === "ticker_bottom") body = <TickerLayout {...props} position={layout === "ticker_top" ? "top" : "bottom"} />;
  else if (layout === "side_left" || layout === "side_right") body = <SidePanelLayout {...props} position={layout === "side_left" ? "left" : "right"} />;
  else if (layout === "video_grid") body = <VideoGridLayout {...props} />;
  else body = <FullLayout {...props} />;

  return (
    <TC.Provider value={T}>
      {!unlocked && <Unlock onUnlock={onUnlock} />}
      {overlay ? (
        <div style={{ fontSize: `${fontScale}rem` }}>
          {body}
          {error && <ErrorRibbon error={error} style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 9 }} />}
        </div>
      ) : (
        <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: T.bg, fontFamily: FONT, fontSize: `${fontScale}rem`, userSelect: "none", overflow: "hidden", color: T.ink }}>
          {body}
          <ErrorRibbon error={error} />
        </div>
      )}
    </TC.Provider>
  );
}
